import { Children, Fragment, isValidElement } from "react";

import { DEFAULT_MIN_WIDTH } from "./constants";
import { normalizeDataType } from "./core/format";

function createOption(name) {
  const Option = () => null;

  Option.gridOption = name;
  Option.displayName = name.charAt(0).toUpperCase() + name.slice(1);

  return Option;
}

export const Column = createOption("column");
export const Paging = createOption("paging");
export const Pager = createOption("pager");
export const FilterRow = createOption("filterRow");
export const HeaderFilter = createOption("headerFilter");
export const GroupPanel = createOption("groupPanel");
export const ColumnChooser = createOption("columnChooser");
export const ColumnChooserSearch = createOption("columnChooserSearch");
export const ColumnFixing = createOption("columnFixing");
export const LoadPanel = createOption("loadPanel");
export const Sorting = createOption("sorting");
export const FilterPanel = createOption("filterPanel");
export const Selection = createOption("selection");
export const RowDragging = createOption("rowDragging");
export const Scrolling = createOption("scrolling");
export const Grouping = createOption("grouping");
export const Export = createOption("export");
export const StateStoring = createOption("stateStoring");
export const RemoteOperations = createOption("remoteOperations");
export const SearchPanel = createOption("searchPanel");
export const Toolbar = createOption("toolbar");
export const Item = createOption("toolbarItem");
export const Editing = createOption("editing");
export const RequiredRule = createOption("validationRule");
export const Summary = createOption("summary");
export const TotalItem = createOption("totalItem");
export const GroupItem = createOption("groupItem");
export const Lookup = createOption("lookup");

const DEVEXTREME_OPTION_ALIASES = {
  columns: "column",
  items: "toolbarItem",
};

function optionNameOf(element) {
  const { type } = element;

  if (!type || typeof type === "string") return null;

  const name = type.gridOption || type.OptionName;

  return DEVEXTREME_OPTION_ALIASES[name] || name || null;
}

export function flattenChildren(children, result = []) {
  Children.forEach(children, child => {
    if (!isValidElement(child)) return;

    if (child.type === Fragment) {
      flattenChildren(child.props.children, result);
      return;
    }

    result.push(child);
  });

  return result;
}

function humanize(dataField) {
  if (!dataField) return "";

  const last = dataField.split(".").pop();

  return last.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/^./, letter => letter.toUpperCase());
}

function defaultAlignment(dataType) {
  if (dataType === "number") return "right";
  if (dataType === "boolean") return "center";

  return "left";
}

function parseColumnChildren(children) {
  const result = {};

  flattenChildren(children).forEach(child => {
    const name = optionNameOf(child);

    if (name === "headerFilter") result.headerFilter = child.props;
    if (name === "lookup") result.lookup = child.props;
  });

  return result;
}

export function normalizeColumn(props, index) {
  const { children, ...rest } = props;
  const nested = parseColumnChildren(children);
  const isSelection = rest.type === "selection";
  const hasData = !!(rest.dataField || rest.calculateCellValue);
  const dataType = normalizeDataType(rest.dataType);
  const allowFiltering = hasData && rest.allowFiltering !== false;
  let id = rest.name || rest.dataField;

  if (!id) id = isSelection ? "__selection" : `__column${index}`;

  return {
    ...rest,
    id,
    index,
    isSelection,
    hasData,
    dataType,
    caption: rest.caption ?? humanize(rest.dataField),
    alignment: rest.alignment || defaultAlignment(dataType),
    minWidth: rest.minWidth ?? DEFAULT_MIN_WIDTH,
    allowFiltering,
    allowHeaderFiltering: hasData && (rest.allowHeaderFiltering ?? allowFiltering),
    allowSorting: hasData && rest.allowSorting !== false,
    allowGrouping: hasData && rest.allowGrouping !== false,
    allowHiding: rest.allowHiding !== false,
    allowResizing: rest.allowResizing !== false,
    allowReordering: rest.allowReordering !== false,
    allowExporting: hasData && rest.allowExporting !== false,
    showInColumnChooser: rest.showInColumnChooser !== false && !isSelection,
    headerFilterDataSource: nested.headerFilter?.dataSource,
    headerFilterAllowSearch: nested.headerFilter?.allowSearch,
  };
}

export function parseGridChildren(children) {
  const columns = [];
  const options = {};
  const toolbarItems = [];

  flattenChildren(children).forEach(child => {
    const name = optionNameOf(child);

    if (!name) return;

    if (name === "column") {
      columns.push(child.props);
      return;
    }

    if (name === "toolbar") {
      flattenChildren(child.props.children).forEach(item => {
        if (optionNameOf(item) === "toolbarItem") toolbarItems.push(item.props);
      });
      options.toolbar = { ...child.props, items: toolbarItems };
      return;
    }

    options[name] = child.props;
  });

  return { columns: columns.map(normalizeColumn), options };
}
